import { z } from 'zod';
import { UsdAmount } from './ai.js';
import { IMPORT_MAX_LATEX, LATEX_FORBIDDEN } from './chilldoc-import.js';
/**
 * "Chuyển thành .chilldoc" từ menu tệp — APM-CVT-a, docs/plan-chilldoc-convert.md §3.1.
 *
 * Danh sách đuôi chuyển được là dữ kiện dùng chung FE↔BE: web chọn bộ chuyển, server có thể
 * cần để chặn sau này. Bộ chuyển thật sống ở web (`features/my-files/convert/`) — loại có ở
 * đây chưa chắc đã có bộ chuyển: menu lọc theo sổ đăng ký của web.
 */
export const CONVERT_KINDS = ['word', 'doc', 'markdown', 'pdf', 'sheet', 'deck', 'ppt'];
const BY_EXTENSION = {
    docx: 'word',
    doc: 'doc',
    md: 'markdown',
    markdown: 'markdown',
    pdf: 'pdf',
    xlsx: 'sheet',
    xls: 'sheet',
    pptx: 'deck',
    ppt: 'ppt',
};
const BY_MIME = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
    'application/msword': 'doc',
    'text/markdown': 'markdown',
    'text/x-markdown': 'markdown',
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'sheet',
    'application/vnd.ms-excel': 'sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'deck',
    'application/vnd.ms-powerpoint': 'ppt',
};
/**
 * Loại bộ chuyển của một tệp, hoặc `null` nếu không chuyển được (kể cả `.chilldoc`).
 *
 * **Đuôi thắng MIME**: tệp có đuôi thì chỉ đuôi quyết định — MIME mà S3 ghi cho tệp cá nhân
 * hay là `application/octet-stream`, và một `ghi-chu.txt` khai `application/pdf` vẫn là chữ
 * thuần. Chỉ tên không có đuôi mới tra MIME.
 */
export function convertibleKind(name, mimeType) {
    const dot = name.lastIndexOf('.');
    // `dot < 1`: không có đuôi, hoặc tên kiểu `.gitignore` (chấm ở đầu không phải đuôi).
    if (dot >= 1 && dot < name.length - 1) {
        return BY_EXTENSION[name.slice(dot + 1).toLowerCase()] ?? null;
    }
    const mime = (mimeType ?? '').toLowerCase().split(';')[0].trim();
    return BY_MIME[mime] ?? null;
}
// ─── Bước AI cho phần khó của Office — APM-CVT-h, plan-chilldoc-convert §7.3 ───────────────
/** Công thức Word tối đa một lượt; phần dư giữ chỗ giữ "không nhập được". */
export const CONVERT_MAX_FORMULAS = 30;
/** XML OMML một công thức (đã bỏ `w:rPr`, namespace thừa) — dài hơn thì bộ chuyển không gửi. */
export const CONVERT_MAX_OMML = 8000;
/** Sheet chưa rõ hàng tiêu đề tối đa một lượt. */
export const CONVERT_MAX_SHEETS = 10;
/** Mẫu đầu bảng của một sheet: hàng × cột × ký tự mỗi ô. */
export const CONVERT_SHEET_SAMPLE_ROWS = 6;
export const CONVERT_SHEET_SAMPLE_COLS = 12;
export const CONVERT_SHEET_SAMPLE_CELL = 80;
export const CONVERT_SHEET_NAME_MAX = 100;
/** Số hàng tiêu đề lớn nhất model được chọn (0 | 1 | 2 — `options` của câu hỏi `sheetHeader`). */
export const CONVERT_MAX_HEADER_ROWS = 2;
const Index = z.number().int().nonnegative();
function uniqueIndexes(items, ctx, path) {
    const seen = new Set();
    items.forEach((item, position) => {
        if (seen.has(item.index)) {
            ctx.addIssue({ code: 'custom', path: [path, position, 'index'], message: 'index trùng' });
        }
        seen.add(item.index);
    });
}
export const ConvertAiFormula = z.object({
    /** Thứ tự công thức trong `word/document.xml` (`WordOmmlPart.index` của web). */
    index: Index,
    omml: z.string().trim().min(1).max(CONVERT_MAX_OMML),
});
export const ConvertAiSheet = z.object({
    /** Chỉ số sheet trong sổ (`SheetHeaderQuestion.sheet` của web). */
    index: Index,
    name: z.string().trim().min(1).max(CONVERT_SHEET_NAME_MAX),
    /** Các hàng đầu của vùng dữ liệu dạng chữ đã định dạng; ô trống là `""`. */
    rows: z
        .array(z.array(z.string().max(CONVERT_SHEET_SAMPLE_CELL)).max(CONVERT_SHEET_SAMPLE_COLS))
        .min(1)
        .max(CONVERT_SHEET_SAMPLE_ROWS),
});
/**
 * Đầu vào của skill `convert_office_parts` (`POST /me/files/convert-plan`): chỉ **chữ** — XML
 * OMML mà bộ đổi tất định (`omml-latex.ts`) không đọc được, và mẫu đầu bảng của sheet mà luật
 * hàng tiêu đề không quyết được. Không ảnh, không tệp: model không cần đọc gì thêm.
 */
export const ConvertAiInput = z
    .object({
    formulas: z.array(ConvertAiFormula).max(CONVERT_MAX_FORMULAS),
    sheets: z.array(ConvertAiSheet).max(CONVERT_MAX_SHEETS),
    runId: z.uuid().optional(),
})
    .superRefine((value, ctx) => {
    uniqueIndexes(value.formulas, ctx, 'formulas');
    uniqueIndexes(value.sheets, ctx, 'sheets');
});
export const ConvertFormulaResult = z.object({
    index: Index,
    /** `null` = không chắc ⇒ giữ chỗ giữ "không nhập được". */
    latex: z.string().trim().max(IMPORT_MAX_LATEX).nullable(),
});
export const ConvertSheetResult = z.object({
    index: Index,
    headerRows: z.number().int().min(0).max(CONVERT_MAX_HEADER_ROWS),
});
export const ConvertAiResult = z.object({
    formulas: z.array(ConvertFormulaResult).max(CONVERT_MAX_FORMULAS),
    sheets: z.array(ConvertSheetResult).max(CONVERT_MAX_SHEETS),
    notes: z.array(z.string().trim().max(200)).max(3),
});
/** Phản hồi của `POST /me/files/convert-plan` — cùng khuôn `ImportAiPlanResult`. */
export const ConvertAiPlanResult = z.object({
    result: ConvertAiResult,
    /** Chuỗi thập phân như mọi số tiền AI khác. `'0'` khi không có lượt LLM. */
    costUsd: UsdAmount,
    partial: z.boolean(),
    runId: z.uuid().optional(),
});
/** `{` `}` cân nhau (bỏ qua `\{` `\}`) — LaTeX lệch ngoặc thì MathLive vẽ rác. */
function balancedBraces(latex) {
    let depth = 0;
    for (let i = 0; i < latex.length; i++) {
        const ch = latex[i];
        if (ch === '\\') {
            i += 1;
            continue;
        }
        if (ch === '{')
            depth += 1;
        else if (ch === '}') {
            depth -= 1;
            if (depth < 0)
                return false;
        }
    }
    return depth === 0;
}
/**
 * Lọc kết quả của model về đúng những gì được hỏi — `finalize` (API) và trước khi áp (web)
 * dùng chung. Chỉ số không có trong input hoặc trả lời hai lần ⇒ bỏ; LaTeX có lệnh cấm
 * (`LATEX_FORBIDDEN`) hoặc lệch ngoặc ⇒ `latex: null` (giữ chỗ giữ), rỗng ⇒ `null`. Trả phần
 * hợp lệ + lý do từng mục bị loại / đổi.
 */
export function sanitizeConvertResult(input, result) {
    const rejected = [];
    const formulaIndexes = new Set(input.formulas.map((formula) => formula.index));
    const sheetIndexes = new Set(input.sheets.map((sheet) => sheet.index));
    const seenFormulas = new Set();
    const formulas = [];
    for (const formula of result.formulas) {
        if (!formulaIndexes.has(formula.index)) {
            rejected.push(`formula ${formula.index}: không có công thức này`);
            continue;
        }
        if (seenFormulas.has(formula.index)) {
            rejected.push(`formula ${formula.index}: trả lời hai lần`);
            continue;
        }
        seenFormulas.add(formula.index);
        const latex = formula.latex?.trim() || null;
        if (latex !== null && LATEX_FORBIDDEN.test(latex)) {
            rejected.push(`formula ${formula.index}: LaTeX chứa lệnh không được phép`);
            formulas.push({ index: formula.index, latex: null });
        }
        else if (latex !== null && !balancedBraces(latex)) {
            rejected.push(`formula ${formula.index}: LaTeX lệch ngoặc { }`);
            formulas.push({ index: formula.index, latex: null });
        }
        else {
            formulas.push({ index: formula.index, latex });
        }
    }
    const seenSheets = new Set();
    const sheets = [];
    for (const sheet of result.sheets) {
        if (!sheetIndexes.has(sheet.index)) {
            rejected.push(`sheet ${sheet.index}: không có sheet này`);
            continue;
        }
        if (seenSheets.has(sheet.index)) {
            rejected.push(`sheet ${sheet.index}: trả lời hai lần`);
            continue;
        }
        seenSheets.add(sheet.index);
        sheets.push({ index: sheet.index, headerRows: sheet.headerRows });
    }
    return { result: { formulas, sheets, notes: result.notes }, rejected };
}
//# sourceMappingURL=convert.js.map